import React, { useState } from 'react';
import Navbar from '../Shared/Navbar';
import api from '../../config/api';
import { useNavigate } from 'react-router-dom';
import './ImportWizard.css';

function ImportWizard({ user, onLogout }) {
  const [step, setStep] = useState(1);
  const [source, setSource] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  const sources = [
    { id: 'local', name: 'Local Device', icon: '💻' },
    { id: 'instagram', name: 'Instagram', icon: '📸' },
    { id: 'google-drive', name: 'Google Drive', icon: '📁' },
    { id: 'dropbox', name: 'Dropbox', icon: '📦' },
    { id: 'adobe-cc', name: 'Adobe CC', icon: '🎨' },
    { id: 'clo-set', name: 'CLO-SET', icon: '👗' },
    { id: 'behance', name: 'Behance', icon: '🎯' }
  ];

  const handleSourceSelect = (sourceId) => {
    setSource(sourceId);
    if (sourceId === 'local') {
      setStep(2);
    } else if (sourceId === 'instagram') {
      // Special handling for Instagram
      handleInstagramConnect();
    } else {
      // Mock OAuth flow for other sources
      alert(`OAuth integration for ${sourceId} would launch here`);
    }
  };

  const handleInstagramConnect = async () => {
    try {
      const response = await api.get('/api/import/oauth/instagram');
      
      // In production, this would open OAuth popup
      const mockInstagramPosts = [
        { id: '1', url: 'https://instagram.com/p/mock1', caption: 'My latest design', likes: 245 },
        { id: '2', url: 'https://instagram.com/p/mock2', caption: 'Collection showcase', likes: 189 },
        { id: '3', url: 'https://instagram.com/p/mock3', caption: 'Behind the scenes', likes: 312 }
      ];
      
      const proceed = window.confirm(
        `Instagram Integration\n\n` +
        `Found ${mockInstagramPosts.length} posts from your portfolio.\n\n` +
        `In production, this would:\n` +
        `1. Open Instagram OAuth login\n` +
        `2. Request permissions (user_profile, user_media)\n` +
        `3. Import your fashion posts automatically\n\n` +
        `Continue with mock import?`
      );
      
      if (proceed) {
        // Simulate Instagram import
        const mockFiles = mockInstagramPosts.map((post, idx) => ({
          id: `instagram-${post.id}`,
          originalName: `instagram-post-${idx + 1}.jpg`,
          filename: `mock-instagram-${post.id}.jpg`,
          path: `/uploads/mock-instagram-${post.id}.jpg`,
          size: 2500000 + Math.random() * 1000000,
          mimetype: 'image/jpeg',
          uploadedAt: new Date().toISOString(),
          source: 'instagram',
          metadata: {
            caption: post.caption,
            likes: post.likes,
            instagramUrl: post.url
          }
        }));
        
        setFiles(mockFiles);
        setStep(3);
      }
    } catch (error) {
      alert('Instagram connection failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleFileUpload = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    setUploading(true);

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await api.post('/api/import/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setFiles(response.data.files);
      setStep(3);
    } catch (error) {
      alert('Upload failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleFinish = () => {
    alert('Files uploaded successfully! Redirecting to curation...');
    navigate('/creator/curation');
  };

  return (
    <div>
      <Navbar user={user} onLogout={onLogout} userType="creator" />
      
      <div className="wizard-container">
        {/* Progress Steps */}
        <div className="wizard-steps">
          <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-label">Select Source</div>
          </div>
          <div className="step-line"></div>
          <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">Upload Files</div>
          </div>
          <div className="step-line"></div>
          <div className={`step ${step >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <div className="step-label">Review & Tag</div>
          </div>
        </div>

        {/* Step Content */}
        <div className="wizard-content">
          {step === 1 && (
            <div className="fade-in">
              <h2>Choose Your Source</h2>
              <p className="text-gray-600 mb-4">Where is your work currently stored?</p>
              
              <div className="source-grid">
                {sources.map(s => (
                  <button
                    key={s.id}
                    className="source-card"
                    onClick={() => handleSourceSelect(s.id)}
                  >
                    <div className="source-icon">{s.icon}</div>
                    <div className="source-name">{s.name}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="fade-in">
              <h2>Upload Files</h2>
              <p className="text-gray-600 mb-4">Select images, videos, PDFs, or 3D files from your device</p>
              
              <div className="upload-zone">
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  accept="image/*,video/*,application/pdf,.ai,.clo"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  disabled={uploading}
                />
                <label htmlFor="file-upload" className="upload-label">
                  <div className="upload-icon">📤</div>
                  <div className="upload-text">
                    {uploading ? (
                      <><strong>Uploading...</strong><span>Please wait</span></>
                    ) : (
                      <><strong>Click to upload</strong><span>or drag and drop files here</span></>
                    )}
                  </div>
                  <div className="upload-hint">
                    JPG, PNG, TIFF, PDF, MP4, MOV (max 50MB each)
                  </div>
                </label>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="fade-in">
              <h2>Files Uploaded Successfully!</h2>
              <p className="text-gray-600 mb-4">{files.length} files have been uploaded</p>
              
              <div className="file-list">
                {files.map(file => (
                  <div key={file.id} className="file-item">
                    <div className="file-icon">✓</div>
                    <div className="file-info">
                      <div className="file-name">{file.originalName}</div>
                      <div className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                    </div>
                    <div className="badge badge-success">Ready</div>
                  </div>
                ))}
              </div>

              <div className="wizard-actions">
                <button onClick={() => setStep(2)} className="btn btn-secondary">
                  Upload More
                </button>
                <button onClick={handleFinish} className="btn btn-primary">
                  Continue to AI Tagging →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImportWizard;
